import { DefaultValues } from 'react-hook-form';
import { MonetaryAmount, NoteReceivable } from '../generated/graphql';
import { ownershipTenantsInput, getOwnershipInterestsFromOwnership } from '../Owner';
import { formOwnershipAllocationToFloat, monetaryAmountDataPointFormDefaultValue } from '../UI';
import { monetaryAmountDataPointValue } from '../DataPoint';
import {
  CreateNoteReceivableServiceInput,
  NoteReceivableFormSubmitData,
  UpdateNoteReceivableValueServiceInput,
  UpdateNoteReceivableServiceInput,
  NoteReceivableFormValues,
} from './types';

export function getNoteReceivableValue(noteReceivable: NoteReceivable): MonetaryAmount | undefined {
  return monetaryAmountDataPointValue(noteReceivable.value.latestDataPoint);
}

export function getNoteReceivableFormDefaultValues(
  noteReceivable: NoteReceivable | null | undefined,
  defaultValues?: Partial<NoteReceivableFormValues>
): DefaultValues<NoteReceivableFormValues> {
  if (!noteReceivable) return { ...defaultValues };

  const ownershipInterest = getOwnershipInterestsFromOwnership(noteReceivable?.ownership);
  const owner = ownershipInterest?.owner;
  const ownerPercentage = ownershipInterest?.percentage;

  return {
    nickname: noteReceivable.name,
    notes: noteReceivable.notes || '',
    estimatedValue: monetaryAmountDataPointFormDefaultValue(noteReceivable.value.latestDataPoint),
    ownerData: { ownerID: owner?.id, ownerType: owner?.ownerType },
    ownershipAllocation: ownerPercentage == null ? null : ownerPercentage * 100,
    pendingFiles: null,
    ...defaultValues,
  };
}

export function getCreateNoteReceivableServiceInputFromFormValues(
  values: NoteReceivableFormValues & {
    householdID: string;
  }
): CreateNoteReceivableServiceInput {
  return {
    files: values.pendingFiles ?? [],
    valueInCents: values.estimatedValue * 100,
    createNoteReceivableMutationInput: {
      householdID: values.householdID,
      noteReceivable: {
        name: values.nickname,
        notes: values.notes,
        ownership: ownershipTenantsInput({
          ownerID: values.ownerData.ownerID,
          ownerType: values.ownerData.ownerType,
          percentage: formOwnershipAllocationToFloat(values.ownershipAllocation),
        }),
      },
    },
  };
}

export function getUpdateNoteReceivableServiceInputFromForm({
  formValues,
  householdID,
  changeToken,
  noteReceivableID,
  valueDataPointGroupID,
}: NoteReceivableFormSubmitData): UpdateNoteReceivableValueServiceInput {
  if (!changeToken) throw new Error('changeToken is required');
  if (!householdID) throw new Error('householdID is required');
  if (!noteReceivableID) throw new Error('noteReceivableID is required');

  return {
    valueInCents: formValues.estimatedValue * 100,
    householdID,
    valueDataPointGroupID,
    noteReceivableID,
  };
}

export function getUpdateNoteReceivableServiceInputFromFormFull({
  formValues,
  householdID,
  changeToken,
  noteReceivableID,
}: NoteReceivableFormSubmitData): UpdateNoteReceivableServiceInput {
  if (!changeToken) throw new Error('changeToken is required');
  if (!householdID) throw new Error('householdID is required');
  if (!noteReceivableID) throw new Error('noteReceivableID is required');

  return {
    files: formValues.pendingFiles ?? undefined,
    valueInCents: formValues.estimatedValue * 100,
    updateNoteReceivableInput: {
      changeToken,
      householdID,
      id: noteReceivableID,
      changes: {
        name: formValues.nickname,
        notes: formValues.notes,
        ownership: ownershipTenantsInput({
          ownerID: formValues.ownerData.ownerID,
          ownerType: formValues.ownerData.ownerType,
          percentage: formOwnershipAllocationToFloat(formValues.ownershipAllocation),
        }),
      },
    },
  };
}
